// Route tree (ARCHITECTURE §8). Foundation-owned: module agents add routes in their own
// src/features/<module>/routes.tsx — those arrays are spread in below, relative to /w/:wsId.
import type { RouteObject } from 'react-router';
import { publicRoutes as workspacePublicRoutes, routes as workspaceRoutes } from '../features/workspace/routes';
import { routes as documentsRoutes } from '../features/documents/routes';
import { routes as moneyRoutes } from '../features/money/routes';
import { routes as giftsRoutes } from '../features/gifts/routes';
import { routes as careRoutes } from '../features/care/routes';
import { routes as healthRoutes } from '../features/health/routes';
import { routes as attorneyRoutes } from '../features/attorney/routes';
import { adminRoutes, routes as platformRoutes } from '../features/platform/routes';
import { RequireAuth, RequirePlatformAdmin, RequireWorkspace } from './guards';
import { AppShell } from './layout/AppShell';
import { RootRedirect } from './pages/RootRedirect';
import { NotFoundInShell, NotFoundPage, RouteErrorPage } from './pages/ErrorPages';
import { PageLoader } from '../components/ui';

const auth = () => import('../features/auth/pages');
const landings = () => import('./pages/SectionLanding');
const specialist = () => import('./pages/SpecialistPage');

/** Screens under /w/:wsId (paths relative). */
export const workspaceChildRoutes: RouteObject[] = [
  // Section landing pages (foundation)
  { path: 'money', lazy: async () => ({ Component: (await landings()).MoneyLanding }) },
  { path: 'health', lazy: async () => ({ Component: (await landings()).HealthLanding }) },
  { path: 'records', lazy: async () => ({ Component: (await landings()).RecordsLanding }) },
  { path: 'more', lazy: async () => ({ Component: (await landings()).MoreLanding }) },
  // Foundation screens
  { path: 'more/security', lazy: async () => ({ Component: (await auth()).SecurityPage }) },
  { path: 'more/help/specialist', lazy: async () => ({ Component: (await specialist()).SpecialistPage }) },
  // Module screens
  ...platformRoutes,
  ...workspaceRoutes,
  ...documentsRoutes,
  ...moneyRoutes,
  ...giftsRoutes,
  ...careRoutes,
  ...healthRoutes,
  ...attorneyRoutes,
  { path: '*', Component: NotFoundInShell },
];


export function createAppRoutes(): RouteObject[] {
  return [
    {
      id: 'root',
      errorElement: <RouteErrorPage />,
      HydrateFallback: () => <PageLoader />,
      children: [
        // Public
        { path: '/sign-in', lazy: async () => ({ Component: (await auth()).SignInPage }) },
        { path: '/sign-in/totp', lazy: async () => ({ Component: (await auth()).TotpPage }) },
        { path: '/auth/verify', lazy: async () => ({ Component: (await auth()).VerifyPage }) },
        { path: '/specialist', lazy: async () => ({ Component: (await specialist()).PublicSpecialistPage }) },
        // Invite acceptance works signed in or out (workspace module, FR-1.3)
        ...workspacePublicRoutes,
        // Signed in
        {
          element: <RequireAuth />,
          children: [
            { path: '/', element: <RootRedirect /> },
            {
              path: '/onboarding',
              lazy: async () => ({ Component: (await import('../features/onboarding/OnboardingPage')).OnboardingPage }),
            },
            { path: '/account/security', lazy: async () => ({ Component: (await auth()).AccountSecurityPage }) },
            { path: '/admin', element: <RequirePlatformAdmin />, children: adminRoutes },
            {
              path: '/w/:wsId',
              element: <RequireWorkspace />,
              children: [{ element: <AppShell />, children: workspaceChildRoutes }],
            },
          ],
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ];
}
